import { useEffect, useRef, useState } from 'react';
import AdditionWorkedExampleTutorial from './AdditionWorkedExampleTutorial';
import SubtractionWorkedExampleTutorial from './SubtractionWorkedExampleTutorial';
import MultiplicationWorkedExampleTutorial from './MultiplicationWorkedExampleTutorial';
import DivisionWorkedExampleTutorial from './DivisionWorkedExampleTutorial';
import CosmicValueCapsule from './CosmicValueCapsule';
import CosmicPracticeImpulse from './CosmicPracticeImpulse';
import PracticeTutorialDevPanel from './PracticeTutorialDevPanel';
import { createGame } from '../game/PhaserGame';

const isDevelopmentBuild = process.env.NODE_ENV === 'development';

export const GameCanvas = ({ student } = {}) => {
  const studentRef = useRef(student);
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const [additionTutorial, setAdditionTutorial] = useState(null);
  const [subtractionTutorial, setSubtractionTutorial] = useState(null);
  const [multiplicationTutorial, setMultiplicationTutorial] = useState(null);
  const [divisionTutorial, setDivisionTutorial] = useState(null);
  const [cosmicCapsule, setCosmicCapsule] = useState(null);
  const [practiceImpulse, setPracticeImpulse] = useState(null);

  useEffect(() => {
    const openTutorial = event => {
      if (event.detail?.tutorial) setAdditionTutorial(event.detail);
    };
    const openSubtractionTutorial = event => {
      if (event.detail?.tutorial) setSubtractionTutorial(event.detail);
    };
    const openMultiplicationTutorial = event => {
      if (event.detail?.tutorial) setMultiplicationTutorial(event.detail);
    };
    const openDivisionTutorial = event => {
      if (event.detail?.tutorial) setDivisionTutorial(event.detail);
    };
    window.addEventListener('tmag:practice-addition-tutorial-open', openTutorial);
    window.addEventListener('tmag:practice-subtraction-tutorial-open', openSubtractionTutorial);
    window.addEventListener('tmag:practice-multiplication-tutorial-open', openMultiplicationTutorial);
    const openCosmicCapsule = event => {
      if (event.detail?.message && event.detail?.sessionId) setCosmicCapsule(event.detail);
    };
    const openPracticeImpulse = event => {
      if (event.detail?.message && event.detail?.sessionId) setPracticeImpulse(event.detail);
    };
    const openDevPracticeImpulse = event => {
      if (!isDevelopmentBuild || !event.detail?.message || !event.detail?.sessionId) return;
      setPracticeImpulse(event.detail);
    };
    const openDevCosmicCapsule = event => {
      if (!isDevelopmentBuild || !event.detail?.message || !event.detail?.sessionId) return;
      setCosmicCapsule(event.detail);
    };
    const startDevFinalPreview = event => {
      if (!isDevelopmentBuild || !event.detail?.devPreview || !gameRef.current) return;
      const game = gameRef.current;
      const preview = event.detail;
      game.registry.set('adaptivePractice', false);
      game.registry.set('currentPlanet', preview.planetId);
      game.registry.set('currentMissionIndex', preview.mission.index);
      game.registry.set('currentMissionData', preview.mission);
      game.registry.set('devPreviewConfig', preview);
      // La previsualización reemplaza la escena activa sin dejar HUDs ni menús
      // del flujo anterior detrás de la secuencia oficial.
      game.scene.getScenes(true).forEach(scene => game.scene.stop(scene.scene.key));
      game.scene.start('LevelScene');
    };
    window.addEventListener('tmag:practice-division-tutorial-open', openDivisionTutorial);
    window.addEventListener('tmag:dev-cosmic-capsule-open', openDevCosmicCapsule);
    window.addEventListener('tmag:practice-cosmic-impulse-open', openPracticeImpulse);
    window.addEventListener('tmag:dev-practice-cosmic-impulse-open', openDevPracticeImpulse);
    window.addEventListener('tmag:dev-final-preview-start', startDevFinalPreview);
    window.addEventListener('tmag:cosmic-value-capsule-open', openCosmicCapsule);
    return () => {
      window.removeEventListener('tmag:practice-addition-tutorial-open', openTutorial);
      window.removeEventListener('tmag:practice-subtraction-tutorial-open', openSubtractionTutorial);
      window.removeEventListener('tmag:practice-multiplication-tutorial-open', openMultiplicationTutorial);
      window.removeEventListener('tmag:practice-division-tutorial-open', openDivisionTutorial);
      window.removeEventListener('tmag:dev-cosmic-capsule-open', openDevCosmicCapsule);
      window.removeEventListener('tmag:practice-cosmic-impulse-open', openPracticeImpulse);
      window.removeEventListener('tmag:dev-practice-cosmic-impulse-open', openDevPracticeImpulse);
      window.removeEventListener('tmag:dev-final-preview-start', startDevFinalPreview);
      window.removeEventListener('tmag:cosmic-value-capsule-open', openCosmicCapsule);
    };
  }, []);

  const completeAdditionTutorial = () => {
    const sessionId = additionTutorial?.sessionId;
    setAdditionTutorial(null);
    window.dispatchEvent(new CustomEvent('tmag:practice-addition-tutorial-complete', { detail: { sessionId } }));
  };

  const completeSubtractionTutorial = () => {
    const sessionId = subtractionTutorial?.sessionId;
    setSubtractionTutorial(null);
    window.dispatchEvent(new CustomEvent('tmag:practice-subtraction-tutorial-complete', { detail: { sessionId } }));
  };

  const completeMultiplicationTutorial = () => {
    const sessionId = multiplicationTutorial?.sessionId;
    setMultiplicationTutorial(null);
    window.dispatchEvent(new CustomEvent('tmag:practice-multiplication-tutorial-complete', { detail: { sessionId } }));
  };

  const completeDivisionTutorial = () => {
    const sessionId = divisionTutorial?.sessionId;
    setDivisionTutorial(null);
    window.dispatchEvent(new CustomEvent('tmag:practice-division-tutorial-complete', { detail: { sessionId } }));
  };

  const completePracticeImpulse = () => {
    const sessionId = practiceImpulse?.sessionId;
    setPracticeImpulse(null);
    if (sessionId) {
      window.dispatchEvent(new CustomEvent('tmag:practice-cosmic-impulse-complete', { detail: { sessionId } }));
    }
  };

  const completeCosmicCapsule = () => {
    const sessionId = cosmicCapsule?.sessionId;
    setCosmicCapsule(null);
    if (sessionId) {
      window.dispatchEvent(new CustomEvent('tmag:cosmic-value-capsule-complete', { detail: { sessionId } }));
    }
  };

  useEffect(() => {
    // Creación diferida cancelable: evita la doble instancia de React StrictMode
    // (el montaje transitorio se cancela antes de crear el juego real).
    let game = null;
    let cancelled = false;
    let sceneWatcher = null;

    const syncGameplayMode = () => {
      const container = containerRef.current;
      if (!container || !game?.scene) return;
      // Solo las partidas de plataformas requieren horizontal.
      // Menús, perfil, mapas y resultados permanecen cómodos en vertical.
      const practiceActive = game.scene.isActive('PracticeScene');
      const playing = game.scene.isActive('LevelScene') || practiceActive;
      const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
      // Una laptop táctil puede tener maxTouchPoints, pero no debe convertirse
      // en un teléfono. iPadOS con UA de Mac se reconoce por sus varios puntos
      // táctiles; Android, iPhone y iPad se detectan de forma explícita.
      const handheldDevice =
        /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) ||
        (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1);
      const practiceHandheld = practiceActive && handheldDevice;
      const frame = container.closest('.tmag-frame');
      const page = container.closest('.tmag-page');

      container.dataset.gameplay = playing ? 'true' : 'false';
      container.dataset.practiceTouch = practiceHandheld ? 'true' : 'false';
      frame?.classList.toggle('tmag-practice-touch-layout', practiceHandheld);
      page?.classList.toggle('tmag-practice-touch-page', practiceHandheld);
    };

    const id = setTimeout(() => {
      if (cancelled || !containerRef.current) return;
      game = createGame(containerRef.current, studentRef.current);
      gameRef.current = game;
      syncGameplayMode();
      sceneWatcher = window.setInterval(syncGameplayMode, 180);
      window.addEventListener('orientationchange', syncGameplayMode);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(id);
      if (sceneWatcher) window.clearInterval(sceneWatcher);
      window.removeEventListener('orientationchange', syncGameplayMode);
      gameRef.current = null;
      if (game) {
        try {
          game.destroy(true);
        } catch (e) {
          /* noop */
        }
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="game-canvas"
      className="tmag-canvas"
      data-gameplay="false"
      tabIndex={0}
    >
      <div className="tmag-orientation-guard" role="status" aria-live="polite">
        <span className="tmag-orientation-phone" aria-hidden="true">↻</span>
        <strong>GIRA TU DISPOSITIVO</strong>
        <p>Usa el teléfono en horizontal para jugar esta misión.</p>
      </div>
      <AdditionWorkedExampleTutorial tutorial={additionTutorial} onComplete={completeAdditionTutorial} />
      <SubtractionWorkedExampleTutorial tutorial={subtractionTutorial} onComplete={completeSubtractionTutorial} />
      <MultiplicationWorkedExampleTutorial tutorial={multiplicationTutorial} onComplete={completeMultiplicationTutorial} />
      <DivisionWorkedExampleTutorial tutorial={divisionTutorial} onComplete={completeDivisionTutorial} />
      {isDevelopmentBuild ? <PracticeTutorialDevPanel /> : null}
      <CosmicPracticeImpulse impulse={practiceImpulse} onComplete={completePracticeImpulse} />
      <CosmicValueCapsule capsule={cosmicCapsule} onComplete={completeCosmicCapsule} />
    </div>
  );
};

export default GameCanvas;
